// Shareable weekly snapshot of the Palworld TCG Price Index.
//
// Users generate these links from /tcg-price-index ("Share this week"). The week
// key (2026-W36) makes the URL permanent and identical for everyone, and the
// page is a self-contained, citable summary: index level, weekly move, market
// cap, breadth and the five biggest gainers and losers.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Check, Copy } from "lucide-react";
import { getPriceBoard } from "@/lib/prices.functions";
import { buildPriceRows, formatMoney } from "@/lib/prices-shared";
import { formatChange, formatLevel, INDEX_BASE } from "@/lib/price-index.shared";
import { buildWeekSnapshot, isoWeekKey, parseWeekKey, type SnapshotMover } from "@/lib/index-snapshot";
import { HoverChart } from "@/components/HoverChart";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { cardLinkProps } from "@/lib/card-link";
import { breadcrumbSchema } from "@/lib/schema";
import { NotFoundView } from "@/components/RouteFallbacks";

const BASE = "https://palstars.com/tcg-index-snapshot";

/** Week keys for the last `n` ISO weeks, newest first. */
function recentWeekKeys(n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i * 7);
    const k = isoWeekKey(d);
    if (!out.includes(k)) out.push(k);
  }
  return out;
}

export const Route = createFileRoute("/tcg-index-snapshot/$week")({
  loader: ({ params }) => {
    const week = parseWeekKey(params.week);
    if (!week) throw notFound();
    return { week };
  },
  head: ({ params, loaderData }) => {
    const week = loaderData?.week ?? parseWeekKey(params.week);
    const url = `${BASE}/${params.week}`;
    if (!week) {
      return {
        meta: [{ title: "Snapshot Not Found | Pal Stars" }, { name: "robots", content: "noindex, follow" }],
      };
    }
    const title = `Palworld TCG Index Snapshot ${week.key}`;
    const description = `Palworld TCG Price Index snapshot for ${week.label}: benchmark level, weekly move, tracked market cap, market breadth and the biggest card gainers and losers.`;
    return {
      meta: [
        { title: `${title} | Pal Stars` },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbSchema([
          ["Home", "/"],
          ["Palworld TCG Price Index", "/tcg-price-index"],
          [title, `/tcg-index-snapshot/${week.key}`],
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <NotFoundView
      title="Snapshot week not found"
      message="Snapshot links use an ISO week key such as 2026-W36. Generate a fresh one from the price index."
    />
  ),
  component: SnapshotPage,
});

function MoverRow({ m }: { m: SnapshotMover }) {
  const up = m.change >= 0;
  return (
    <Link
      {...cardLinkProps(m.code)}
      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-2 hover:border-sky-400/40"
    >
      <CardImage src={m.image} alt={m.name} width={40} height={56} className="h-14 w-10 rounded" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{m.name}</span>
        <span className="flex items-center gap-2 text-[11px] text-white/50">
          <RarityBadge code={m.code} /> {m.code}
        </span>
      </span>
      <span className="text-right">
        <span className="block text-sm font-semibold">{formatMoney(m.market, "USD")}</span>
        <span className={`flex items-center justify-end gap-1 text-xs ${up ? "text-emerald-400" : "text-rose-400"}`}>
          {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {formatChange(m.change)}
        </span>
      </span>
    </Link>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="text-[11px] uppercase tracking-wide text-white/45">{label}</div>
      <div className={`mt-1 text-xl font-extrabold ${tone ?? ""}`}>{value}</div>
    </div>
  );
}

function SnapshotPage() {
  const { week } = Route.useLoaderData();
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    refetchOnMount: "always",
    staleTime: 10 * 60 * 1000,
  });
  const rows = useMemo(() => data?.rows ?? buildPriceRows([]), [data]);
  const snap = useMemo(() => buildWeekSnapshot(rows, week), [rows, week]);
  const [copied, setCopied] = useState(false);
  const url = `${BASE}/${week.key}`;

  const cite = `Palworld TCG Price Index, week ${week.key} (${week.label}): ${formatLevel(snap.level)} (${formatChange(
    snap.change,
  )} on the week), equal-weight base ${INDEX_BASE} across ${snap.constituents} tracked printings. Source: ${url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cite);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const up = snap.change >= 0;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld TCG Index Snapshot — {week.key}</h1>
      <p className="aeo-answer mt-3 text-white/75">
        For {week.label}, the Palworld TCG Price Index closed at {formatLevel(snap.level)}, {up ? "up" : "down"}{" "}
        {formatChange(snap.change).replace("+", "")} on the week across {snap.constituents} tracked card printings, with{" "}
        {snap.advancing} printings advancing and {snap.declining} declining. This snapshot is fixed to its week, so the
        link keeps showing the same market picture.
      </p>

      <AffiliateDisclosure className="mt-4" updated={data?.updated} />

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Index level" value={formatLevel(snap.level)} />
        <Stat
          label="Week change"
          value={formatChange(snap.change)}
          tone={up ? "text-emerald-400" : "text-rose-400"}
        />
        <Stat label="Tracked market cap" value={`$${Math.round(snap.marketCap).toLocaleString("en-US")}`} />
        <Stat label="Basket (one of each)" value={formatMoney(snap.basket, "USD")} />
      </div>

      {snap.hasData && snap.points.length > 1 ? (
        <section className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <h2 className="text-lg font-bold">Index through the week</h2>
          <HoverChart
            labels={snap.points.map((p) => p.day)}
            series={[{ id: "level", label: "Index level", color: "#38bdf8", values: snap.points.map((p) => p.level) }]}
            baseline={INDEX_BASE}
            format={(v) => formatLevel(v)}
            ariaLabel={`Palworld TCG Price Index for week ${week.key}`}
            area
          />
        </section>
      ) : (
        <p className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/60">
          No daily index points were recorded inside {week.label} yet. The{" "}
          <Link to="/tcg-price-index" className="text-sky-300 hover:underline">
            live price index
          </Link>{" "}
          shows the current level, and this snapshot fills in once the week has synced data.
        </p>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <section>
          <h2 className="text-lg font-bold">Biggest gainers</h2>
          <div className="mt-2 space-y-2">
            {snap.gainers.length ? (
              snap.gainers.map((m) => <MoverRow key={m.code} m={m} />)
            ) : (
              <p className="text-sm text-white/50">No cards gained inside this week.</p>
            )}
          </div>
        </section>
        <section>
          <h2 className="text-lg font-bold">Biggest fallers</h2>
          <div className="mt-2 space-y-2">
            {snap.losers.length ? (
              snap.losers.map((m) => <MoverRow key={m.code} m={m} />)
            ) : (
              <p className="text-sm text-white/50">No cards fell inside this week.</p>
            )}
          </div>
        </section>
      </div>

      <section className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <h2 className="text-lg font-bold">Cite this snapshot</h2>
        <p className="mt-2 text-sm text-white/70">{cite}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-2 rounded-full bg-sky-400/20 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-400/30"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Citation copied" : "Copy citation"}
          </button>
          <Link
            to="/tcg-price-index"
            className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-sky-400/40"
          >
            Live price index
          </Link>
          <Link
            to="/tcg-index-snapshot/$week"
            params={{ week: isoWeekKey() }}
            className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-sky-400/40"
          >
            This week's snapshot
          </Link>
        </div>
        {/* Archive links keep every dated snapshot in the sitemap internally
            linked instead of orphaned. */}
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-white/70">Weekly snapshot archive</h3>
          <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {recentWeekKeys(12).map((k) => (
              <li key={k}>
                <Link
                  to="/tcg-index-snapshot/$week"
                  params={{ week: k }}
                  className="font-mono text-white/60 hover:text-sky-200 hover:underline"
                >
                  {k}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
